import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InteractionFeed } from './InteractionFeed';
import { AddMessageModal } from './AddMessageModal';
import { HubCommunicationCard } from './HubCommunicationCard';
import { AcceptedOfferSection } from './AcceptedOfferSection';
import { useHubStore } from '../../stores/hubStore';
import { useProfileStore } from '../../stores/profileStore';
import { HubContext, HubQuoteRequest } from '../../types/hub';
import { HubParticipantPill } from './HubParticipantPill';
import SubmissionList from '../common/SubmissionList';
import { useHubInteractionsByParticipant } from '../../hooks/useHubInteractionsByParticipant';
import { useHubUnreadPolling } from '../../hooks/useHubUnreadPolling';

interface RecruiterHubViewProps {
  context: HubContext;
  /** Query param: which participant (quote) to focus. */
  selectedQuoteId?: string | null;
  onSelectedQuoteIdChange?: (quoteId: string | null) => void;
  /** Read-only (e.g. admin hub): same feed as recruiter, no compose. */
  readOnly?: boolean;
  /** Admin hub: RPC “view as” profile (null = recruiter / client). */
  adminEffectiveViewerId?: string | null;
}

type SelectableQuote = {
  quote_id: string | null;
  freelancer_id: string;
  quote_owner_first_name: string;
  quote_owner_last_name: string | null;
  quote_owner_avatar_url: string | null;
};

const mergeSelectableQuotes = (items: SelectableQuote[]): SelectableQuote[] => {
  const grouped = new Map<string, SelectableQuote>();

  for (const item of items) {
    const existing = grouped.get(item.freelancer_id);
    if (!existing) {
      grouped.set(item.freelancer_id, item);
      continue;
    }

    const shouldReplace =
      (!existing.quote_id && Boolean(item.quote_id)) ||
      (!existing.quote_owner_avatar_url && Boolean(item.quote_owner_avatar_url));

    if (shouldReplace) {
      grouped.set(item.freelancer_id, item);
    }
  }

  return Array.from(grouped.values());
};

const fromQuoteRequest = (request: HubQuoteRequest): SelectableQuote | null => {
  if (!request?.freelancer?.id) return null;
  return {
    quote_id: null,
    freelancer_id: request.freelancer.id,
    quote_owner_first_name: request.freelancer.first_name,
    quote_owner_last_name: null,
    quote_owner_avatar_url: request.freelancer.avatar_url ?? null,
  };
};

export const RecruiterHubView: React.FC<RecruiterHubViewProps> = ({
  context,
  selectedQuoteId,
  onSelectedQuoteIdChange,
  readOnly = false,
  adminEffectiveViewerId = null,
}) => {
  const { t } = useTranslation(['hub', 'jobs']);
  const { profile } = useProfileStore();
  const { hubUnreadCountByParticipantKey, fetchHubUnreadCounts } = useHubStore();
  const [showAddMessage, setShowAddMessage] = useState(false);
  const [selectedFreelancerId, setSelectedFreelancerId] = useState<string | null>(null);

  const isProjectView = context.view_type === 'recruiter_project';
  const acceptedQuote = context.accepted_quote;

  const selectableQuotes: SelectableQuote[] = useMemo(() => {
    if (isProjectView) {
      return acceptedQuote
        ? [
            {
              quote_id: acceptedQuote.id,
              freelancer_id: acceptedQuote.freelancer_id,
              quote_owner_first_name: acceptedQuote.quote_owner_first_name,
              quote_owner_last_name: acceptedQuote.quote_owner_last_name,
              quote_owner_avatar_url: acceptedQuote.quote_owner_avatar_url,
            },
          ]
        : [];
    }

    const quotesFromContext =
      context.quotes?.map(quote => ({
        quote_id: quote.id,
        freelancer_id: quote.freelancer_id,
        quote_owner_first_name: quote.quote_owner_first_name,
        quote_owner_last_name: quote.quote_owner_last_name,
        quote_owner_avatar_url: quote.quote_owner_avatar_url,
      })) ?? [];

    const quotesFromJob =
      context.job.quotes?.map(quote => ({
        quote_id: quote.id,
        freelancer_id: quote.quote_owner.id,
        quote_owner_first_name: quote.quote_owner.first_name,
        quote_owner_last_name: quote.quote_owner.last_name,
        quote_owner_avatar_url: quote.quote_owner.avatar_url ?? null,
      })) ?? [];

    const mergedQuotes = mergeSelectableQuotes([...quotesFromContext, ...quotesFromJob]);
    if (mergedQuotes.length > 0) {
      return mergedQuotes;
    }

    const fallbackFromQuoteRequests =
      context.job.quote_requests
        ?.map(fromQuoteRequest)
        .filter((q): q is SelectableQuote => q != null) ?? [];

    return mergeSelectableQuotes(fallbackFromQuoteRequests);
  }, [
    isProjectView,
    acceptedQuote,
    context.quotes,
    context.job.quotes,
    context.job.quote_requests,
  ]);

  useEffect(() => {
    if (isProjectView) {
      setSelectedFreelancerId(acceptedQuote?.freelancer_id ?? null);
      return;
    }

    if (selectableQuotes.length === 0) {
      setSelectedFreelancerId(null);
      return;
    }

    setSelectedFreelancerId(previousId => {
      if (previousId && selectableQuotes.some(quote => quote.freelancer_id === previousId)) {
        return previousId;
      }

      if (selectedQuoteId) {
        const quoteMatchedByRoute = selectableQuotes.find(
          quote => quote.quote_id === selectedQuoteId
        );
        if (quoteMatchedByRoute) {
          return quoteMatchedByRoute.freelancer_id;
        }
      }

      return selectableQuotes[0].freelancer_id;
    });
  }, [acceptedQuote?.freelancer_id, selectedQuoteId, isProjectView, selectableQuotes]);

  const selectedQuote = useMemo(
    () => selectableQuotes.find(quote => quote.freelancer_id === selectedFreelancerId) ?? null,
    [selectableQuotes, selectedFreelancerId]
  );

  const isQuoteRequestOnlyView = !isProjectView && !selectedQuote?.quote_id;

  const isSelectedQuoteRequestAccepted = useMemo(() => {
    if (!isQuoteRequestOnlyView || !selectedFreelancerId) {
      return true;
    }

    const matchingRequest =
      context.job.quote_requests?.find(request => request.freelancer.id === selectedFreelancerId) ??
      null;

    return matchingRequest?.status === 'accepted';
  }, [context.job.quote_requests, isQuoteRequestOnlyView, selectedFreelancerId]);

  const interactionQuoteId = isProjectView
    ? (acceptedQuote?.id ?? null)
    : (selectedQuote?.quote_id ?? null);

  const participantDescriptors = useMemo(
    () =>
      selectableQuotes.map(quote => ({
        participantKey: quote.quote_id ?? `request:${quote.freelancer_id}`,
        quoteId: quote.quote_id,
        participantId: quote.freelancer_id,
      })),
    [selectableQuotes]
  );

  const currentParticipantKey =
    selectedQuote != null
      ? (selectedQuote.quote_id ?? `request:${selectedQuote.freelancer_id}`)
      : (participantDescriptors[0]?.participantKey ?? null);

  const {
    getInteractions,
    getLoading,
    getLoadingMore,
    getHasMore,
    loadMore,
    refresh,
    refreshParticipantNow,
  } = useHubInteractionsByParticipant(
    context.job.id,
    participantDescriptors,
    currentParticipantKey,
    adminEffectiveViewerId
  );

  const interactions = currentParticipantKey ? getInteractions(currentParticipantKey) : [];
  const loading = currentParticipantKey ? getLoading(currentParticipantKey) : false;
  const loadingMore = currentParticipantKey ? getLoadingMore(currentParticipantKey) : false;
  const hasMore = currentParticipantKey ? getHasMore(currentParticipantKey) : false;
  const loadMoreForCurrent = currentParticipantKey ? loadMore(currentParticipantKey) : () => {};
  const refreshCurrent = currentParticipantKey ? refresh(currentParticipantKey) : () => {};

  const recipientIds: string[] = selectedQuote ? [selectedQuote.freelancer_id] : [];

  useEffect(() => {
    fetchHubUnreadCounts(context.job.id, { adminEffectiveViewerId });
  }, [context.job.id, fetchHubUnreadCounts, adminEffectiveViewerId]);

  useHubUnreadPolling(
    readOnly ? undefined : context.job.id,
    readOnly ? 0 : 60_000,
    refreshParticipantNow
  );

  const handleMessageSent = () => {
    refreshCurrent();
    fetchHubUnreadCounts(context.job.id, { adminEffectiveViewerId });
  };

  if (!profile?.id) return null;

  const hasSubmittedQuotes = context.job.quotes?.some(q => q.status === 'submitted') ?? false;

  const handleSelectQuote = (quote: SelectableQuote) => {
    setSelectedFreelancerId(quote.freelancer_id);
    onSelectedQuoteIdChange?.(quote.quote_id ?? null);
  };

  const header =
    selectableQuotes.length > 0 ? (
      <>
        {selectableQuotes.map(quote => (
          <HubParticipantPill
            key={`${quote.freelancer_id}-${quote.quote_id ?? 'request'}`}
            id={quote.freelancer_id}
            first_name={quote.quote_owner_first_name}
            avatar_url={quote.quote_owner_avatar_url}
            isActive={quote.freelancer_id === selectedFreelancerId}
            onClick={() => handleSelectQuote(quote)}
            unreadCount={
              hubUnreadCountByParticipantKey[quote.quote_id ?? `request:${quote.freelancer_id}`] ??
              0
            }
          />
        ))}
      </>
    ) : undefined;

  return (
    <>
      <div className="mb-6">
        <AcceptedOfferSection hub={context} />
      </div>
      {hasSubmittedQuotes && (
        <div className="mb-6">
          <SubmissionList hub={context} />
        </div>
      )}
      <HubCommunicationCard
        sectionTitle={
          isProjectView
            ? t('project_tracking', { ns: 'hub' })
            : t('interaction_history', { ns: 'hub' })
        }
        header={header}
        addMessageButton={
          readOnly
            ? undefined
            : {
                onClick: () => setShowAddMessage(true),
                disabled: recipientIds.length === 0 || !isSelectedQuoteRequestAccepted,
                label: t('addMessage'),
              }
        }
        addMessageModal={
          !readOnly && showAddMessage ? (
            <AddMessageModal
              jobId={context.job.id}
              recipientIds={recipientIds}
              quoteId={interactionQuoteId}
              onClose={() => setShowAddMessage(false)}
              onSent={handleMessageSent}
            />
          ) : undefined
        }
      >
        <InteractionFeed
          key={interactionQuoteId ?? 'all'}
          interactions={interactions}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={loadMoreForCurrent}
          currentUserId={profile.id}
          context={context}
        />
      </HubCommunicationCard>
    </>
  );
};
